import React, { useEffect, useRef } from 'react';
import { Dropdown, DropdownAnchor, DropdownContent } from '@fiverr-private/dropdown';
import { Container, Stack } from '@fiverr-private/layout_components';
import { BI_EVENTS, ChatGptIcon, ClaudeIcon } from '@fiverr-private/layout_menus';
import { AppI18n } from '../../../i18n';
import { TriggerButton } from '../../blocks/TriggerButton';
import { reportConnectToAi } from './reportConnectToAi';
import { ConnectToAiContent } from './ConnectToAiContent';
import { overlapClass } from './ConnectToAiNavigation.ve.css';

export const ConnectToAiNavigation = () => {
    const wrapperRef = useRef<HTMLElement>(null);
    const wasViewed = useRef(false);

    useEffect(() => {
        const element = wrapperRef.current;
        const reportView = () => {
            if (!wasViewed.current) {
                wasViewed.current = true;
                reportConnectToAi({ type: BI_EVENTS.viewedButton, action: 'impression' });
            }
        };

        if (!element) {
            return undefined;
        }

        if (typeof IntersectionObserver === 'undefined') {
            reportView();
            return undefined;
        }

        // the button is hidden below the lg breakpoint, so only count it once it is actually visible
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                reportView();
                observer.disconnect();
            }
        });
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    const handleTriggerClick = () => {
        reportConnectToAi({ type: BI_EVENTS.clickedButton, action: 'click' });
    };

    const handleOpenChanged = (isOpen: boolean) => {
        if (isOpen) {
            reportConnectToAi({ type: BI_EVENTS.viewedDropdown, action: 'impression' });
        }
    };

    return (
        <Container ref={wrapperRef} display="inlineFlex">
            <Dropdown position="bottom-end" onOpenChanged={handleOpenChanged}>
                <DropdownAnchor embed>
                    <TriggerButton onClick={handleTriggerClick}>
                        <AppI18n k="connect_to_ai.title" />
                        <Stack gap="0" alignItems="center">
                            <Stack position="relative" zIndex="2" className={overlapClass}>
                                <ChatGptIcon size={20} shape="circle" />
                            </Stack>
                            <Stack position="relative" zIndex="1">
                                <ClaudeIcon size={20} shape="circle" />
                            </Stack>
                        </Stack>
                    </TriggerButton>
                </DropdownAnchor>
                <DropdownContent closeOnClickOutside width="293px" variant="sections">
                    <ConnectToAiContent />
                </DropdownContent>
            </Dropdown>
        </Container>
    );
};
